import { markRaw, reactive, toRaw } from "vue";
import { PRIVILEGES } from "@/constants/privileges";
import { usePrivilegesStore } from "@/stores/privileges";
import { loadVideoPolicy } from "@/api/videoUpload";
import { createVideoUploader } from "@/utils/videoUploader";
import { isUploadBusy, isUploadInFlight } from "@/utils/videoUploadPlan";
import {
  noteVideoAccept as acceptFor,
  noteVideoLimit,
  noteVideoOffered,
  notePatientId,
  validateNoteVideoFile,
} from "@/utils/noteAttachments";

// A failed policy call is asked again at the next opportunity, but not at every one.
const POLICY_RETRY_MS = 30000;

const isEntry = (item) => !!item && item.isNoteVideoEntry === true;

let entrySeq = 0;

// What the browser says when the page is closed or left mid-upload.
function warnBeforeUnload(event) {
  event.preventDefault();
  event.returnValue = "";
  return "";
}

/**
 * The video side of NotePopup: a note takes videos (and nothing else), each
 * uploaded in chunks the moment it is picked (keyon/video_upload.php, domain
 * note, for the patient the note is on) and named by uid when the note is
 * saved. The upload itself is the shared one (videoUploader.js); this holds
 * the list of them, the server's policy, and what a person may do with each.
 *
 * Needs of the component it is mixed into: `patient` (the prop) and
 * `sessionActor` (the signed-in person, as NotePopup has it).
 *
 * The policy (GET video_upload.php?action=config) is never asked for on mount:
 * HomeGrid mounts one popup per patient row. It is asked for at the first
 * contact with a popup (wantNoteVideoPolicy), and only by a person who may
 * write notes (privilege 13), so the control stays out of an environment, a
 * server or a person that cannot use it.
 */
export default {
  data() {
    return {
      // Entries: an object that stands where a File would (name, size, type)
      // and carries its uploader, held outside Vue's reactivity (it is the
      // uploader's own state), `upload` (the snapshot a chip shows) and
      // `patientVisible` (staff only until the person says otherwise).
      noteVideoEntries: [],
      noteVideoPolicy: null,
      noteVideoPolicyState: "idle", // idle | loading | loaded | failed
      noteVideoPolicyFailedAt: 0,
      noteVideoUnloadHandler: null,
    };
  },
  computed: {
    noteVideoMayWrite() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.WRITE_NOTES);
    },
    // Exactly what saveNote sends as the patient, as text: keyon matches the
    // upload's patient against the save's, character for character.
    noteVideoPatientId() {
      const id = notePatientId(this.patient);
      return id === undefined || id === null ? "" : String(id);
    },
    // The attach control: the server says a note takes video, and this person may write one.
    noteVideoAvailable() {
      return noteVideoOffered(this.noteVideoPolicy) && this.noteVideoMayWrite && this.noteVideoPatientId !== "";
    },
    noteVideoAccept() {
      return acceptFor(this.noteVideoPolicy);
    },
    noteVideoFull() {
      return this.noteVideoEntries.length >= noteVideoLimit(this.noteVideoPolicy);
    },
    // Whether Save must wait: a video that is not ready. A failed upload counts,
    // or the note would be saved without the video the person attached.
    noteVideosBusy() {
      return this.noteVideoEntries.some((entry) => isUploadBusy(entry.upload.status));
    },
    // Reads the entries' states, so it changes as an upload does.
    noteVideosInFlight() {
      return this.noteVideoEntries.some((entry) => isUploadInFlight(entry.upload.status));
    },
  },
  watch: {
    // The beforeunload prompt is armed only while a video is going up.
    noteVideosInFlight(active) {
      this.armNoteVideoUnloadGuard(active);
    },
  },
  beforeUnmount() {
    this.armNoteVideoUnloadGuard(false);
    this.discardNoteVideos();
  },
  methods: {
    /** First contact with the popup (pointer or keyboard): ask for the policy, if this person could use it. */
    wantNoteVideoPolicy() {
      if (!this.noteVideoMayWrite || this.noteVideoPatientId === "") return null;
      return this.ensureNoteVideoPolicy();
    },

    /** The video policy, once. A failure is remembered for 30 seconds, then asked again at the next contact. */
    async ensureNoteVideoPolicy() {
      if (this.noteVideoPolicyState === "loading" || this.noteVideoPolicyState === "loaded") return this.noteVideoPolicy;
      if (this.noteVideoPolicyState === "failed" && Date.now() - this.noteVideoPolicyFailedAt < POLICY_RETRY_MS) {
        return this.noteVideoPolicy;
      }
      this.noteVideoPolicyState = "loading";
      let policy = null;
      try {
        policy = await loadVideoPolicy();
      } catch (_) {
        policy = null;
      }
      if (policy) {
        this.noteVideoPolicy = policy;
        this.noteVideoPolicyState = "loaded";
      } else {
        this.noteVideoPolicyState = "failed";
        this.noteVideoPolicyFailedAt = Date.now();
      }
      return this.noteVideoPolicy;
    },

    /** SweetAlert is not a thing NotePopup has; it says things with alert(), as everything else in it does. */
    noteVideoAlert(text) {
      alert(text);
    },

    /**
     * The files a person picked. Each is held to the server's rules (type, size,
     * how many a note takes) and becomes an entry that uploads at once.
     */
    onNoteVideosPicked(files) {
      for (const file of Array.isArray(files) ? files : []) {
        const raw = toRaw(file) || file;
        if (this.noteVideoEntries.some((e) => e.name === raw.name && e.size === raw.size)) continue;
        const verdict = validateNoteVideoFile(raw, this.noteVideoPolicy, { videosInNote: this.noteVideoEntries.length });
        if (!verdict.ok) {
          this.noteVideoAlert(verdict.message);
          // The note is full for the rest of the pick too, and one sentence says so.
          if (verdict.code === "too_many_videos") break;
          continue;
        }
        this.noteVideoEntries.push(this.createNoteVideoEntry(raw));
      }
    },

    createNoteVideoEntry(rawFile) {
      const actor = (this.sessionActor && this.sessionActor.unique_ID) || "";
      const patientId = this.noteVideoPatientId;
      const uploader = markRaw(
        createVideoUploader({
          file: rawFile,
          // The resume key is the person's and the patient's: one file picked
          // for two patients is two uploads, not one session begun again.
          uid: `${actor}|${patientId}`,
          policy: toRaw(this.noteVideoPolicy),
          domain: "note",
          patientId,
        })
      );
      const entry = reactive({
        isNoteVideoEntry: true,
        id: (entrySeq += 1),
        name: rawFile.name,
        size: rawFile.size,
        type: rawFile.type,
        uploader,
        upload: uploader.getSnapshot(),
        patientVisible: false,
      });
      uploader.subscribe((snapshot) => {
        entry.upload = snapshot;
      });
      uploader.start();
      return entry;
    },

    /**
     * A chip's own buttons: Cancel (drop it and let keyon release it), Verify again, Retry.
     * The entry's Remove sends "cancel" too, in any state, so a failed or ready video can go.
     */
    onNoteVideoAction({ entry, action }) {
      if (!isEntry(entry)) return;
      if (action === "cancel") {
        const index = this.noteVideoEntries.findIndex((e) => toRaw(e) === toRaw(entry));
        if (index >= 0) this.noteVideoEntries.splice(index, 1);
        this.cancelNoteVideo(entry);
      } else if (action === "verify") {
        entry.uploader.verifyAgain();
      } else if (action === "retry") {
        entry.uploader.retry();
      }
    },

    /** Who may play this video once the note is saved: staff only (false) or the patient too (true). */
    onNoteVideoVisible({ entry, visible }) {
      if (isEntry(entry)) entry.patientVisible = visible === true;
    },

    /** The uids of the videos that are ready, in the order they were attached: what the save names. */
    noteVideoUids() {
      return this.noteVideoEntries
        .filter((entry) => entry.upload.status === "ready" && entry.upload.fileUid)
        .map((entry) => entry.upload.fileUid);
    },
    /** Those of them the patient may play (the rest are staff only). */
    noteVideoVisibleUids() {
      return this.noteVideoEntries
        .filter((entry) => entry.upload.status === "ready" && entry.upload.fileUid && entry.patientVisible)
        .map((entry) => entry.upload.fileUid);
    },

    // Not awaited: the person has moved on. keyon abandons their own video while nothing references it.
    cancelNoteVideo(entry) {
      Promise.resolve(entry.uploader.cancel()).catch(() => null);
    },

    /**
     * After a note that was saved: the videos keyon attached are the note's now
     * (the uploader lets go without discarding them); one keyon could not
     * attach has been released by keyon and is cancelled here so nothing is
     * left behind. The list and the choices start empty for the next note.
     * @param {{unbound?: string[]}} result notesApi.saveNote's
     */
    finishNoteVideos(result) {
      const unbound = new Set((result && result.unbound) || []);
      this.noteVideoEntries.forEach((entry) => {
        if (entry.upload.status !== "ready" || unbound.has(entry.upload.fileUid)) {
          this.cancelNoteVideo(entry);
        } else {
          entry.uploader.release();
        }
      });
      this.noteVideoEntries = [];
    },

    /** Cancel, and the popup going away: every unclaimed upload is cancelled (a running one stops, a finished one is discarded). */
    discardNoteVideos() {
      this.noteVideoEntries.forEach((entry) => this.cancelNoteVideo(entry));
      this.noteVideoEntries = [];
    },

    armNoteVideoUnloadGuard(on) {
      if (typeof window === "undefined") return;
      if (on && !this.noteVideoUnloadHandler) {
        this.noteVideoUnloadHandler = warnBeforeUnload;
        window.addEventListener("beforeunload", warnBeforeUnload);
      } else if (!on && this.noteVideoUnloadHandler) {
        window.removeEventListener("beforeunload", this.noteVideoUnloadHandler);
        this.noteVideoUnloadHandler = null;
      }
    },
  },
};
